import React from 'react'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { Trans, useTranslation } from 'react-i18next'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { Dialog, QuestionMarkOutlined, Tooltip } from '@netbasequid/core'
import { useDisplayTheme } from 'hooks/useUiState'
import {
  selectIsSubscribeModalOpen,
  selectIsSubscribeStatusLoading,
  selectV4ScheduledReportInfo
} from 'features/scheduledReport/selectors'
import { closeScheduledReportSubscriptionModal } from 'features/scheduledReport/scheduledReportSlice'
import { subscribeDashboardAlerts } from 'features/scheduledReport/asyncThunks'
import { get24TimeDisplay, getGMTString } from 'utils/timeZoneUtil'
import { useDashboardData } from 'hooks/useDashboardState'
import AlertFrequency, { AlertFrequencyStrings } from 'enums/AlertFrequency'
import { Theme } from 'features/ui/types'
import SavingButton from 'components/visualization/SavingButton'
import {
  StyledContainer,
  StyledTable,
  StyledRow,
  StyledRowLeft,
  StyledRowRight,
  StyledSemiboldText
} from './index.css'
import { scheduledReportAlertDayOfMonthString, scheduledReportFrequencyString } from './utils'

const ThemeInfoRow = () => {
  const theme = useDisplayTheme() as Theme

  const scheduledReportInfo = useAppSelector(selectV4ScheduledReportInfo)
  const { t } = useTranslation()

  return (
    <StyledRow>
      {scheduledReportInfo?.colorMode === 'LIGHT' ? t('lightMode') : t('darkMode')}
      <Tooltip mode={theme} content={<>{t('scheduledReportSubscriptionModalTooltip')}</>}>
        <QuestionMarkOutlined color={sys[theme].text.placeholder} fontSize={16} />
      </Tooltip>
    </StyledRow>
  )
}

const FrequencyTableContent = ({ frequency }: { frequency?: AlertFrequencyStrings }) => {
  const scheduledReportInfo = useAppSelector(selectV4ScheduledReportInfo)
  const { t } = useTranslation()
  const dashboardData = useDashboardData()

  switch (frequency) {
    case AlertFrequency.DAILY:
      return (
        <>
          <StyledRowLeft>
            <StyledRow>{t('generateReport')}</StyledRow>
            <StyledRow>{t('mode')}</StyledRow>
            <StyledRow>{t('at')}</StyledRow>
          </StyledRowLeft>
          <StyledRowRight>
            <StyledRow>{scheduledReportFrequencyString(frequency)}</StyledRow>
            <ThemeInfoRow />
            <StyledRow>
              {get24TimeDisplay(scheduledReportInfo?.alertHourFirst)}{' '}
              {getGMTString(dashboardData?.ownerInfo?.timeZone)}
            </StyledRow>
          </StyledRowRight>
        </>
      )
    case AlertFrequency.TWICE_DAILY:
      return (
        <>
          <StyledRowLeft>
            <StyledRow>{t('generateReport')}</StyledRow>
            <StyledRow>{t('mode')}</StyledRow>
            <StyledRow>{t('at')}</StyledRow>
            <StyledRow>{t('And')}</StyledRow>
          </StyledRowLeft>
          <StyledRowRight>
            <StyledRow>{scheduledReportFrequencyString(frequency)}</StyledRow>
            <ThemeInfoRow />
            <StyledRow>
              {get24TimeDisplay(scheduledReportInfo?.alertHourFirst)}{' '}
              {getGMTString(dashboardData?.ownerInfo?.timeZone)}
            </StyledRow>
            <StyledRow>
              {get24TimeDisplay(scheduledReportInfo?.alertHourSecond)}{' '}
              {getGMTString(dashboardData?.ownerInfo?.timeZone)}
            </StyledRow>
          </StyledRowRight>
        </>
      )
    case AlertFrequency.WEEKLY:
      return (
        <>
          <StyledRowLeft>
            <StyledRow>{t('generateReport')}</StyledRow>
            <StyledRow>{t('mode')}</StyledRow>
            <StyledRow>{t('at')}</StyledRow>
            <StyledRow>{t('every')}</StyledRow>
          </StyledRowLeft>
          <StyledRowRight>
            <StyledRow>{scheduledReportFrequencyString(frequency)}</StyledRow>
            <ThemeInfoRow />
            <StyledRow>
              {get24TimeDisplay(scheduledReportInfo?.alertHourFirst)}{' '}
              {getGMTString(dashboardData?.ownerInfo?.timeZone)}
            </StyledRow>
            <StyledRow>{scheduledReportInfo?.dayOfWeek}</StyledRow>
          </StyledRowRight>
        </>
      )
    case AlertFrequency.MONTHLY:
      return (
        <>
          <StyledRowLeft>
            <StyledRow>{t('generateReport')}</StyledRow>
            <StyledRow>{t('mode')}</StyledRow>
            <StyledRow>{t('at')}</StyledRow>
            <StyledRow>{t('every')}</StyledRow>
          </StyledRowLeft>
          <StyledRowRight>
            <StyledRow>{scheduledReportFrequencyString(frequency)}</StyledRow>
            <ThemeInfoRow />
            <StyledRow>
              {get24TimeDisplay(scheduledReportInfo?.alertHourFirst)}{' '}
              {getGMTString(dashboardData?.ownerInfo?.timeZone)}
            </StyledRow>
            <StyledRow>
              {scheduledReportAlertDayOfMonthString(scheduledReportInfo?.dayOfMonth)}
            </StyledRow>
          </StyledRowRight>
        </>
      )

    default:
      return <></>
  }
}

const ReportSubscriptionModal = ({ dashboardId }: { dashboardId: number }) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const scheduledReportInfo = useAppSelector(selectV4ScheduledReportInfo)
  const isOpen = useAppSelector(selectIsSubscribeModalOpen)
  const isLoading = useAppSelector(selectIsSubscribeStatusLoading)

  const onClose = () => {
    dispatch(closeScheduledReportSubscriptionModal())
  }

  return (
    <Dialog mode={theme} open={isOpen} onClose={onClose} disableBackdropClick width="600px">
      <Dialog.DialogTitle>{t('confirmSubscription')}</Dialog.DialogTitle>
      <Dialog.DialogContent>
        <StyledContainer>
          <div>
            <Trans i18nKey="subscriptionModalMsg">
              <StyledSemiboldText>{t('subscribe')}</StyledSemiboldText>
            </Trans>
          </div>
          <StyledTable>
            <FrequencyTableContent frequency={scheduledReportInfo?.frequency} />
          </StyledTable>
          <div>{t('subscriptionModalFooter')}</div>
        </StyledContainer>
      </Dialog.DialogContent>

      <Dialog.DialogActions
        okButtonProps={{
          disabled: isLoading,
          children: isLoading ? <SavingButton /> : t('subscribe'),
          onClick: () => {
            dispatch(subscribeDashboardAlerts(dashboardId)).then((subscribeResultAction) => {
              if (subscribeDashboardAlerts.fulfilled.match(subscribeResultAction)) {
                onClose()
              }
            })
          }
        }}
        closeButtonProps={{
          color: 'secondary',
          children: t('cancel')
        }}
      />
    </Dialog>
  )
}

export default ReportSubscriptionModal
